import { convertToDateObjIfPossible } from '~/services/transformations/other-helpers'
import { CustomDate, CustomDateTime } from '~/services/graphql/generated'
import { add, format } from 'date-fns'

export function isFutureDate(
    customDate: CustomDate | CustomDateTime,
    useLocalTimeZone?: boolean
) {
    const now = new Date()

    const date = convertToDateObjIfPossible(customDate, useLocalTimeZone)
    if (date instanceof Date) {
        return date > now
    } else if (date.year) {
        if (date.__typename === 'CustomDateTime' && date.hour) {
            if (process.env.NODE_ENV === 'development') {
                throw Error(
                    'Unexpected time value found in partial date. ' +
                        'Partial dates are only supported for dates without times'
                )
            }
            return false
        }

        // partial date
        const currentYear = now.getFullYear()
        const currentMonth = now.getMonth() + 1 // months are zero-indexed in JS
        return (
            date.year > currentYear ||
            (date.year === currentYear &&
                date.month !== null &&
                date.month > currentMonth)
        )
    }

    return false
}

export function isTodaysDateUtc(date: CustomDate | CustomDateTime) {
    const todayUtc: CustomDate = getTodayUtcAsCustomDate()
    return (
        date.year === todayUtc.year &&
        date.month === todayUtc.month &&
        date.day === todayUtc.day
    )
}

function getTodayUtcAsCustomDate() {
    const todayInUtc = convertToUtc(new Date())
    todayInUtc.setHours(0, 0, 0, 0)

    return {
        __typename: 'CustomDate',
        year: todayInUtc.getFullYear(),
        month: todayInUtc.getMonth() + 1,
        day: todayInUtc.getDate(),
    } as const
}

export function isPastDate(date: CustomDate) {
    return !isFutureDate(date)
}

export function convertToDateFromUnixTime(unixTime: number) {
    return new Date(unixTime * 1000)
}

export function getMonthName(
    monthNum: number,
    monthFormat: 'short' | 'long' = 'long'
) {
    // note: month name from graphql is 1-indexed, whereas months in JS Dates are zero-indexed
    const tmpDate = new Date(1970, monthNum - 1, 1)
    return format(tmpDate, monthFormat === 'short' ? 'LLL' : 'LLLL')
}

// Convert a date object to UTC.
// The return value isn't really a UTC date, but rather a local date shifted to match the UTC time
export function convertToUtc(time: Date) {
    return add(time, { minutes: time.getTimezoneOffset() })
}
